import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import filter from 'lodash/filter';
import React, { createContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import { ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import { CampaignType } from 'src/actions/campaigns/campaignsResource';
import EmptyState from 'src/presentation/components/pages/proactiveCommunications/components/shared/EmptyState';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import OverflowMenu, {
  OverflowMenuItem,
  OverflowMenuLink,
  OverflowMenuText,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import RecipientCollectionsEditor from './RecipientCollectionsEditor';
import recipientCollectionsResource from 'src/actions/campaigns/recipientCollectionsResource';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import TablePage from 'src/presentation/components/pages/shared/TablePage';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import { usePopoverMenu } from '@ad-scripts/presentation/components/common/menu';
import useRecipientCollections from 'src/presentation/components/pages/proactiveCommunications/hooks/useRecipientCollections';
import useRouter from 'src/presentation/hooks/useRouter';

type RecipientCollectionContextType = {
  addRecipientFormComponent?: any;
  campaignType: CampaignType;
  campaignContentDetailsComponent?: any;
};

export const RecipientCollectionContext = createContext<RecipientCollectionContextType>({
  campaignType: CampaignType.SMS,
});

const dataAidPrefix = 'recipient-collections-';

const TooltipForName = name => (
  <Tooltip delay={750} message={name}>
    {name}
  </Tooltip>
);

const columnConfig = [
  { attr: 'createdAtString', label: 'Created On' },
  { attr: 'name', label: 'Recipients CSV', customRenderer: item => TooltipForName(get(item, 'name', '')) },
  { attr: 'status', label: 'Status', customRenderer: item => capitalize(item.status) },
  { attr: 'progressString', label: 'Progress' },
];

const tabConfig = [
  { label: 'Scheduled', filter: item => item.status === 'SCHEDULED', key: `${dataAidPrefix}-tab-scheduled` },
  { label: 'In Progress', filter: item => item.status === 'ACTIVE', key: `${dataAidPrefix}-tab-active` },
  { label: 'Completed', filter: item => item.status === 'COMPLETED', key: `${dataAidPrefix}-tab-completed` },
  { label: 'Stopped', filter: item => item.status === 'CANCELED', key: `${dataAidPrefix}-tab-canceled` },
];

type RecipientCollectionsType = {
  ({
    addRecipientFormComponent,
    backButtonLabel,
    campaignContentDetailsComponent,
    campaignType,
    root,
  }: {
    addRecipientFormComponent: any;
    backButtonLabel: string;
    campaignContentDetailsComponent: any;
    campaignType: CampaignType;
    root: string;
  }): JSX.Element | null;
};

const RecipientCollections: RecipientCollectionsType = ({
  addRecipientFormComponent,
  backButtonLabel,
  campaignContentDetailsComponent,
  campaignType,
  root,
}) => {
  const {
    match,
  }: {
    match: { url: string; params: { campaignId: string } };
  } = useRouter() as any;

  const dispatch = useDispatch();

  const update = recipientCollectionsResource.effects.update;
  const remove = recipientCollectionsResource.effects.remove;

  const [isStopModalVisible, setIsStopModalVisible] = useState(false);
  const [stopItem, setStopItem] = useState(null);
  const openStopModal = (item: any) => {
    setStopItem(item);
    setIsStopModalVisible(true);
  };
  const handleStopCancel = () => setIsStopModalVisible(false);
  const handleStopSubmit = () => {
    const id = get(stopItem, 'id');
    const fileName = get(stopItem, 'name');
    dispatch(
      update(
        { id, campaignId: match.params.campaignId },
        { status: 'CANCELED', fileName },
        {
          patch: true,
          disableWindowBack: true,
        }
      )
    );
    setIsStopModalVisible(false);
  };

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const openDeleteModal = (item: any) => {
    setDeleteItem(item);
    setIsDeleteModalVisible(true);
  };
  const handleDeleteCancel = () => setIsDeleteModalVisible(false);

  const handleDeleteSubmit = () => {
    const id = get(deleteItem, 'id');
    const fileName = get(deleteItem, 'name');
    dispatch(remove({ campaignId: match.params.campaignId, id, fileName }));
    setIsDeleteModalVisible(false);
  };

  const stopOrDeleteMenuItem = ({ item, openStopModal, openDeleteModal, onClose }) =>
    item.status === 'ACTIVE' || item.status === 'SCHEDULED' ? (
      <OverflowMenuItem
        id="stop"
        data-aid={`${dataAidPrefix}stop`}
        onClick={() => {
          onClose();
          openStopModal(item);
        }}
      >
        <OverflowMenuText>Stop outreach</OverflowMenuText>
      </OverflowMenuItem>
    ) : (
      <OverflowMenuItem
        id="delete"
        data-aid={`${dataAidPrefix}delete`}
        onClick={() => {
          onClose();
          openDeleteModal(item);
        }}
      >
        <OverflowMenuText>Delete outreach</OverflowMenuText>
      </OverflowMenuItem>
    );

  const RecipientCollectionActions = ({ item, openStopModal, openDeleteModal }) => {
    const { onClose, ...overFlowProps } = usePopoverMenu();
    return (
      <StyledOverflowMenu>
        <OverflowMenu id={item.id} onClose={onClose} {...overFlowProps}>
          <OverflowMenuItem id="details" data-aid={`${dataAidPrefix}details`}>
            <OverflowMenuLink to={`${root}/${item.id}`}>View details</OverflowMenuLink>
          </OverflowMenuItem>
          {stopOrDeleteMenuItem({ item, openStopModal, openDeleteModal, onClose })}
        </OverflowMenu>
      </StyledOverflowMenu>
    );
  };

  const { campaign, recipientCollections, loadingState } = useRecipientCollections({
    campaignId: match.params.campaignId,
    campaignType,
  });

  const filterFunc = (items, searchString) =>
    searchString
      ? filter(items, item => {
          return (item?.name ?? '').toLowerCase().indexOf(searchString?.toLowerCase()) > -1;
        })
      : items;

  const actions = (
    <RouterLinkButton data-aid={`${dataAidPrefix}addRecipients`} to={`${root}/new`}>
      Add Recipients
    </RouterLinkButton>
  );

  const authorString =
    campaign?.createdByString && campaign?.createdAtString
      ? `Created on ${campaign?.createdAtString} by ${campaign?.createdByString}`
      : null;
  const description = (
    <span>
      {get(campaign, 'description')}
      <br />
      {authorString}
    </span>
  );

  const renderRowActions = ({ item }) => {
    return <RecipientCollectionActions openStopModal={openStopModal} openDeleteModal={openDeleteModal} item={item} />;
  };

  const context = { addRecipientFormComponent, campaignType, campaignContentDetailsComponent };
  const emptyStateRender = <EmptyState title="Add recipients and set your outreach date" actionsRender={actions} />;

  return (
    <RecipientCollectionContext.Provider value={context}>
      <TablePage
        actions={actions}
        backButtonUrl={getBackUrl(root)}
        backButtonLabel={backButtonLabel}
        columnConfig={columnConfig}
        description={description}
        editor={RecipientCollectionsEditor}
        emptyStateFullHeightLayout
        emptyStateRender={emptyStateRender}
        errors={[]}
        filterFunc={filterFunc}
        isLoading={isLoadingStatus(loadingState)}
        items={recipientCollections}
        name={campaign?.name || ''}
        resetErrors={recipientCollectionsResource.actions.resetErrors}
        root={root}
        renderRowActions={renderRowActions}
        data-aid={`${dataAidPrefix}page`}
        tabConfig={tabConfig}
      />
      {isStopModalVisible ? (
        <StyledConfirmOrCancelModal
          data-aid="stop-recipient-collection"
          onCancel={handleStopCancel}
          onSubmit={handleStopSubmit}
          submitButtonType={ButtonTypes.DANGER}
          submitText="Yes, stop"
          cancelText="Cancel"
          title="You sure about that?"
          text={
            <>
              You{"'"}re about to stop <strong>{get(stopItem, 'name')}</strong> outreach,
              <br /> and it cannot be undone.
            </>
          }
        ></StyledConfirmOrCancelModal>
      ) : null}
      {isDeleteModalVisible ? (
        <StyledConfirmOrCancelModal
          data-aid="delete-recipient-collection"
          onCancel={handleDeleteCancel}
          onSubmit={handleDeleteSubmit}
          submitButtonType={ButtonTypes.DANGER}
          submitText="Yes, delete"
          cancelText="Cancel"
          title="You sure about that?"
          text={
            <>
              You{"'"}re about to delete <strong>{get(deleteItem, 'name')}</strong> outreach,
              <br /> and it cannot be undone.
            </>
          }
        ></StyledConfirmOrCancelModal>
      ) : null}
    </RecipientCollectionContext.Provider>
  );
};

const getBackUrl = root => {
  const parts = root.split('/');
  parts.splice(parts.length - 2, 2);
  return parts.join('/');
};

const StyledOverflowMenu = styled.div`
  visibility: hidden;

  & svg {
    fill: ${p => p.theme.colors.gray600};
  }
`;

export default RecipientCollections;
